/**
 * Parse [item/type] markup in LOTSOG lesson/course text into flashcard fronts/backs.
 *
 * Examples:
 *   [photosynthesis/definition] Process plants use to convert light into chemical energy.
 *   [Hamlet/character] Prince of Denmark, grieving his father, hesitant to act.
 *   [mitosis/explanation] How one cell divides into two genetically identical daughter cells.
 *   [Treaty of Versailles/event] Ended WWI; imposed reparations on Germany (1919).
 *   [Battle of Hastings/date] 14 October 1066.
 *   [Causes of WWI/eventmodel] Nationalism, alliances, imperialism, assassination of Franz Ferdinand.
 */

/** @typedef {'definition'|'explanation'|'character'|'event'|'date'|'eventmodel'|string} LotsogFlashcardMarkupType */

const MARKUP_TAG_RE = /\[([^/\]\n]+)\/([^\]\n]+)\]/g;

const FRONT_BY_TYPE = Object.freeze({
  definition: (item) => `What is ${item}?`,
  vocabulary: (item) => `What is ${item}?`,
  explanation: (item) => `Explain ${item}.`,
  concept: (item) => `What is the key idea of ${item}?`,
  fact: (item) => `State the fact about ${item}.`,
  process: (item) => `Describe the process: ${item}.`,
  character: (item) => `Describe ${item} (as covered so far).`,
  event: (item) => `What happened in ${item}?`,
  date: (item) => `When did ${item} occur (date or period)?`,
  eventmodel: (item) => `Explain the causes, course, and effects of ${item}.`,
  event_model: (item) => `Explain the causes, course, and effects of ${item}.`,
});

/**
 * @param {string} item
 * @param {string} cardType
 * @returns {string}
 */
export const buildFlashcardFrontFromMarkup = (item, cardType) => {
  const label = String(item || '').trim();
  const type = String(cardType || '').trim().toLowerCase();
  if (!label) return '';
  const fn = FRONT_BY_TYPE[type];
  if (typeof fn === 'function') return fn(label);
  const prettyType = type.replace(/_/g, ' ');
  return `${label} (${prettyType})`;
};

/**
 * @param {string} text
 * @returns {Array<{ item: string, cardType: string, back: string, rawType: string }>}
 */
export const extractFlashcardMarkupEntries = (text) => {
  const source = String(text || '');
  if (!source.trim()) return [];

  const markers = [];
  let match = MARKUP_TAG_RE.exec(source);
  while (match) {
    markers.push({
      index: match.index,
      item: String(match[1] || '').trim(),
      rawType: String(match[2] || '').trim(),
      cardType: String(match[2] || '').trim().toLowerCase(),
      tagLength: match[0].length,
    });
    match = MARKUP_TAG_RE.exec(source);
  }
  MARKUP_TAG_RE.lastIndex = 0;

  const entries = [];
  for (let i = 0; i < markers.length; i += 1) {
    const m = markers[i];
    const start = m.index + m.tagLength;
    const end = i + 1 < markers.length ? markers[i + 1].index : source.length;
    const back = source.slice(start, end).trim();
    if (!m.item || !m.cardType || !back) continue;
    entries.push({
      item: m.item,
      cardType: m.cardType,
      rawType: m.rawType,
      back,
      front: buildFlashcardFrontFromMarkup(m.item, m.cardType),
    });
  }
  return entries;
};

/**
 * @param {string} item
 * @returns {string}
 */
export const slugFlashcardMarkupItem = (item) =>
  String(item || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48) || 'item';

/**
 * @param {number|string} lessonDay
 * @param {string} item
 * @param {string} cardType
 * @returns {string}
 */
export const buildFlashcardCardIdFromMarkup = (lessonDay, item, cardType) => {
  const day = Number(lessonDay) || 1;
  return `fc_d${day}_${slugFlashcardMarkupItem(item)}_${String(cardType || 'note').toLowerCase()}`;
};

/**
 * Walk curriculum/track lessons map and extract all markup cards.
 * @param {Record<string, { narration?: string, content?: string, title?: string, lessonId?: string }>} lessonsMap
 * @returns {Array<{ lessonDay: number, lessonId: string, item: string, cardType: string, front: string, back: string, cardId: string }>}
 */
export const extractFlashcardMarkupFromLessonsMap = (lessonsMap) => {
  const out = [];
  if (!lessonsMap || typeof lessonsMap !== 'object') return out;

  Object.entries(lessonsMap).forEach(([dayKey, lesson]) => {
    const lessonDay = Number(dayKey) || 1;
    const lessonId = String(lesson?.lessonId || lesson?.id || dayKey).trim();
    const text = [lesson?.narration, lesson?.content, lesson?.summary]
      .filter(Boolean)
      .join('\n\n');
    const entries = extractFlashcardMarkupEntries(text);
    entries.forEach((entry, index) => {
      out.push({
        lessonDay,
        lessonId,
        sourceLessonId: lessonId,
        unlockLessonDay: lessonDay,
        item: entry.item,
        cardType: entry.cardType,
        front: entry.front,
        back: entry.back,
        cardId: buildFlashcardCardIdFromMarkup(lessonDay, entry.item, entry.cardType),
        order: lessonDay * 100 + index,
        createdBy: 'markup',
      });
    });
  });

  return out.sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * @param {Record<string, { completedAt?: * }>} lessonsMap
 * @param {number} unlockLessonDay
 * @returns {boolean}
 */
export const isFlashcardLessonUnlockedForStudent = (lessonsMap, unlockLessonDay) => {
  const day = Number(unlockLessonDay) || 1;
  const lesson = lessonsMap?.[String(day)] || lessonsMap?.[day];
  if (!lesson) return false;
  return Boolean(lesson.completedAt);
};

/**
 * @param {object[]} cards
 * @param {Record<string, { completedAt?: * }>} lessonsMap
 */
export const filterFlashcardsUnlockedByLessonComplete = (cards, lessonsMap) =>
  (cards || []).filter(
    (c) => !c.hidden && isFlashcardLessonUnlockedForStudent(lessonsMap, c.unlockLessonDay)
  );
